/**
 * PricingStep Component
 * Step 3: Program pricing configuration
 */

import React from 'react';
import { FaDollarSign, FaPlus, FaTrash, FaPercent } from 'react-icons/fa';
import { FEE_INTERVALS } from '../constants/gameFormats';
import styles from './StepStyles.module.css';
import pricingStyles from './PricingStep.module.css';

const PricingStep = ({
  formData,
  errors,
  updateField,
  updateArrayItem,
  addArrayItem,
  removeArrayItem
}) => {
  const handleSimplePriceToggle = (useSimple) => {
    updateField('useSimplePrice', useSimple);
    if (useSimple && !formData.simplePrice) {
      // Initialize with first registration fee amount if available
      const firstFee = formData.cost.registrationFees[0];
      if (firstFee?.amount) {
        updateField('simplePrice', firstFee.amount);
      }
    }
  };

  // Add registration fee
  const addRegistrationFee = () => {
    addArrayItem('cost.registrationFees', {
      amount: '',
      discount: { percentage: '', validUntil: '' },
      startDate: '',
      endDate: ''
    });
  };

  // Add fee structure
  const addFeeStructure = () => {
    addArrayItem('cost.feeStructure', {
      amount: '',
      interval: 'Per Program'
    });
  };

  return (
    <div className={styles.stepContainer}>
      {/* Pricing Mode Toggle */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FaDollarSign /> Pricing
        </h3>

        <div className={pricingStyles.modeSelector}>
          <button
            type="button"
            className={`${pricingStyles.modeBtn} ${formData.useSimplePrice ? pricingStyles.active : ''}`}
            onClick={() => handleSimplePriceToggle(true)}
          >
            <span className={pricingStyles.modeIcon}>💵</span>
            <span className={pricingStyles.modeTitle}>Simple Price</span>
            <span className={pricingStyles.modeDesc}>Single fixed price</span>
          </button>

          <button
            type="button"
            className={`${pricingStyles.modeBtn} ${!formData.useSimplePrice ? pricingStyles.active : ''}`}
            onClick={() => handleSimplePriceToggle(false)}
          >
            <span className={pricingStyles.modeIcon}>📊</span>
            <span className={pricingStyles.modeTitle}>Advanced Pricing</span>
            <span className={pricingStyles.modeDesc}>Multiple fees, discounts</span>
          </button>
        </div>
      </div>

      {/* Simple Price */}
      {formData.useSimplePrice && (
        <div className={styles.section}>
          <div className={styles.formGroup}>
            <label className={`${styles.label} ${styles.required}`}>
              Program Price
            </label>
            <div className={pricingStyles.priceInputWrapper}>
              <span className={pricingStyles.currencySymbol}>$</span>
              <input
                type="number"
                className={`${styles.input} ${pricingStyles.priceInput} ${errors.simplePrice ? styles.error : ''}`}
                value={formData.simplePrice}
                onChange={(e) => updateField('simplePrice', e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            {errors.simplePrice && (
              <span className={styles.errorText}>{errors.simplePrice}</span>
            )}
            <span className={styles.hint}>
              This is the one-time fee participants pay to join the program
            </span>
          </div>
        </div>
      )}

      {/* Advanced Pricing */}
      {!formData.useSimplePrice && (
        <>
          {/* Registration Fees */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Registration Fees</h3>
            <p className={styles.hint} style={{ marginTop: '-12px', marginBottom: '16px' }}>
              Set different pricing tiers based on registration dates
            </p>

            {formData.cost.registrationFees.map((fee, index) => (
              <div key={index} className={styles.arrayItem}>
                {index > 0 && (
                  <button
                    type="button"
                    className={styles.removeItemBtn}
                    onClick={() => removeArrayItem('cost.registrationFees', index)}
                  >
                    <FaTrash />
                  </button>
                )}

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={`${styles.label} ${styles.required}`}>
                      Amount
                    </label>
                    <div className={pricingStyles.priceInputWrapper}>
                      <span className={pricingStyles.currencySymbol}>$</span>
                      <input
                        type="number"
                        className={`${styles.input} ${pricingStyles.priceInput}`}
                        value={fee.amount}
                        onChange={(e) => updateArrayItem('cost.registrationFees', index, 'amount', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Discount %
                    </label>
                    <div className={pricingStyles.priceInputWrapper}>
                      <input
                        type="number"
                        className={`${styles.input} ${pricingStyles.discountInput}`}
                        value={fee.discount?.percentage || ''}
                        onChange={(e) => {
                          const newDiscount = {
                            ...fee.discount,
                            percentage: e.target.value
                          };
                          updateArrayItem('cost.registrationFees', index, 'discount', newDiscount);
                        }}
                        placeholder="0"
                        min="0"
                        max="100"
                      />
                      <span className={pricingStyles.percentSymbol}><FaPercent /></span>
                    </div>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Registration Opens
                    </label>
                    <input
                      type="date"
                      className={styles.input}
                      value={fee.startDate}
                      onChange={(e) => updateArrayItem('cost.registrationFees', index, 'startDate', e.target.value)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Registration Closes
                    </label>
                    <input
                      type="date"
                      className={styles.input}
                      value={fee.endDate}
                      onChange={(e) => updateArrayItem('cost.registrationFees', index, 'endDate', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className={styles.addItemBtn}
              onClick={addRegistrationFee}
            >
              <FaPlus /> Add Registration Period
            </button>
          </div>

          {/* Fee Structure (Recurring) */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Additional Fees</h3>
            <p className={styles.hint} style={{ marginTop: '-12px', marginBottom: '16px' }}>
              Add recurring or per-game fees if applicable
            </p>

            {formData.cost.feeStructure.map((fee, index) => (
              <div key={index} className={styles.arrayItem}>
                {index > 0 && (
                  <button
                    type="button"
                    className={styles.removeItemBtn}
                    onClick={() => removeArrayItem('cost.feeStructure', index)}
                  >
                    <FaTrash />
                  </button>
                )}

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Amount</label>
                    <div className={pricingStyles.priceInputWrapper}>
                      <span className={pricingStyles.currencySymbol}>$</span>
                      <input
                        type="number"
                        className={`${styles.input} ${pricingStyles.priceInput}`}
                        value={fee.amount}
                        onChange={(e) => updateArrayItem('cost.feeStructure', index, 'amount', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Interval</label>
                    <select
                      className={styles.select}
                      value={fee.interval}
                      onChange={(e) => updateArrayItem('cost.feeStructure', index, 'interval', e.target.value)}
                    >
                      {FEE_INTERVALS.map(interval => (
                        <option key={interval.value} value={interval.value}>
                          {interval.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className={styles.addItemBtn}
              onClick={addFeeStructure}
            >
              <FaPlus /> Add Fee
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PricingStep;









